/**
 * Tenant-configurable date/time locale settings.
 *
 * Sourced from siteLocation in aos-config-manager. All getters fall back
 * to the legacy Helsinki defaults when config is not yet loaded so module
 * import order stays safe; the getters are evaluated lazily on every call
 * so that switching tenants takes effect immediately.
 *
 * Timezone misconfiguration (present-but-invalid value) falls back to UTC
 * — not the host's local time — and surfaces in both `console.warn` and
 * Sentry tagged with the tenant identifier, so the misconfiguration shows
 * up in observability rather than silently shifting timestamps. The
 * tenant-absent path stays on the Helsinki default to keep unmigrated
 * tenants working through the rollout (AOS-502 acceptance criterion).
 */

import * as Sentry from '@sentry/react'
import { getSiteLocation, isInitialized } from 'aos-config-loader'
import moment from 'moment-timezone'

export const FALLBACK_TIMEZONE = 'Europe/Helsinki'
export const INVALID_TIMEZONE_FALLBACK = 'UTC'
export const FALLBACK_DATE_FORMAT = 'DD.MM.YYYY'
export const FALLBACK_TIME_FORMAT = 'HH:mm'
export const FALLBACK_DATE_TIME_FORMAT = 'DD.MM.YYYY, HH:mm'
export const FALLBACK_DATE_MONTH_TIME_FORMAT = 'DD.MM, HH:mm'

type TimezoneRead =
    | { state: 'absent' }
    | { state: 'invalid-type'; rawValue: unknown }
    | { state: 'empty' }
    | { state: 'string'; value: string }

const readTimezoneField = (): TimezoneRead => {
    if (!isInitialized()) {
        return { state: 'absent' }
    }
    const config = getSiteLocation() as Record<string, unknown>
    if (!('timezone' in config)) {
        return { state: 'absent' }
    }
    const value = config.timezone
    if (typeof value !== 'string') {
        return { state: 'invalid-type', rawValue: value }
    }
    if (value.length === 0) {
        return { state: 'empty' }
    }
    return { state: 'string', value }
}

const readSiteLocaleField = (key: string): string | undefined => {
    if (!isInitialized()) {
        return undefined
    }
    const value = (getSiteLocation() as Record<string, unknown>)[key]
    return typeof value === 'string' && value.length > 0 ? value : undefined
}

/**
 * Best-effort tenant/site identifier for log + observability tags. Falls
 * back to "unknown" rather than throwing — this code path runs on every
 * date render and must not crash the app if siteLocation is incomplete.
 */
export const getTenantIdentifier = (): string => {
    if (!isInitialized()) {
        return 'unknown'
    }
    const config = getSiteLocation() as Record<string, unknown>
    const networkAirport =
        typeof config.networkAirport === 'object' && config.networkAirport !== null
            ? (config.networkAirport as { name?: unknown })
            : {}
    const networkName = typeof networkAirport.name === 'string' ? networkAirport.name : undefined
    const airports =
        typeof config.airports === 'object' &&
        config.airports !== null &&
        !Array.isArray(config.airports)
            ? (config.airports as Record<string, unknown>)
            : {}
    const firstAirport = Object.keys(airports)[0]
    if (networkName && firstAirport) {
        return `${networkName}:${firstAirport}`
    }
    return networkName ?? firstAirport ?? 'unknown'
}

const reportedInvalidTimezones = new Set<string>()

const reportInvalidTimezone = (offending: unknown, reason: string): void => {
    const offendingKey = typeof offending === 'string' ? offending : JSON.stringify(offending)
    if (reportedInvalidTimezones.has(offendingKey)) {
        return
    }
    reportedInvalidTimezones.add(offendingKey)
    const tenant = getTenantIdentifier()
    const message = `[appLocale] timezone misconfigured (${reason}); offending=${JSON.stringify(
        offending,
    )}, tenant=${tenant}; falling back to ${INVALID_TIMEZONE_FALLBACK}.`
    // eslint-disable-next-line no-console
    console.warn(message)
    try {
        Sentry.withScope(scope => {
            scope.setTag('appLocale.tenant', tenant)
            scope.setTag('appLocale.invalidTimezone', String(offending))
            scope.setLevel('warning')
            scope.setContext('appLocale', { reason, offending, tenant })
            Sentry.captureMessage(`appLocale: invalid timezone configuration (${reason})`)
        })
    } catch {
        // Sentry not initialized (tests, early boot) — the console.warn above
        // is enough on its own.
    }
}

/** Test-only: clears the warn-once dedup set so each spec starts fresh. */
export const __resetAppLocaleWarnings = (): void => {
    reportedInvalidTimezones.clear()
}

export const getAppTimezone = (): string => {
    const read = readTimezoneField()
    switch (read.state) {
        case 'absent':
            return FALLBACK_TIMEZONE
        case 'string':
            if (moment.tz.zone(read.value)) {
                return read.value
            }
            reportInvalidTimezone(read.value, 'not a known IANA zone')
            return INVALID_TIMEZONE_FALLBACK
        case 'empty':
            reportInvalidTimezone('', 'empty string')
            return INVALID_TIMEZONE_FALLBACK
        case 'invalid-type':
            reportInvalidTimezone(read.rawValue, 'not a string')
            return INVALID_TIMEZONE_FALLBACK
    }
}

export const getDateFormat = (): string => readSiteLocaleField('dateFormat') ?? FALLBACK_DATE_FORMAT

export const getTimeFormat = (): string => readSiteLocaleField('timeFormat') ?? FALLBACK_TIME_FORMAT

export const getDateTimeFormat = (): string =>
    readSiteLocaleField('dateTimeFormat') ?? FALLBACK_DATE_TIME_FORMAT

export const getDateMonthTimeFormat = (): string =>
    readSiteLocaleField('dateMonthTimeFormat') ?? FALLBACK_DATE_MONTH_TIME_FORMAT
